import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useBackDismiss } from '../lib/backStack.js';

/**
 * Mobile-first dialog: a bottom sheet on phones, a centred panel from `sm` up.
 *
 * Dismissal has three routes — the close button, Escape, and a tap anywhere on
 * the backdrop — because on a phone the backdrop tap is the gesture people
 * actually reach for, and a sheet that only closes via a small × is a trap.
 */
export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}) {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  const close = useCallback(() => {
    if (dismissible) onClose?.();
  }, [dismissible, onClose]);

  // The Android back button closes the sheet, like a tap outside it does.
  useBackDismiss(open, close);

  // Escape to dismiss, and lock the body so the page behind doesn't scroll
  // under the sheet on iOS.
  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Move focus into the dialog so screen readers and keyboards follow it.
    const raf = requestAnimationFrame(() => panelRef.current?.focus());

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      cancelAnimationFrame(raf);
      previouslyFocused.current?.focus?.();
    };
  }, [open, close]);

  if (!open) return null;

  const width =
    { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' }[size] ?? 'sm:max-w-lg';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="presentation"
      // Backdrop dismissal: only when the press starts *and* ends on the
      // backdrop itself, so dragging a selection out of the panel doesn't
      // close it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
      onTouchEnd={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="absolute inset-0 bg-leaf-900/40 backdrop-blur-sm animate-backdrop-in"
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={`relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-crate bg-surface shadow-lift outline-none animate-sheet-up sm:rounded-crate ${width}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Grab handle — the affordance that says "this sheet can be dismissed". */}
        {dismissible && (
          <div className="flex justify-center pt-3 sm:hidden">
            <span className="h-1.5 w-10 rounded-full bg-oat-darker" aria-hidden="true" />
          </div>
        )}

        {(title || dismissible) && (
          <div className="flex items-start justify-between gap-4 px-5 pb-4 pt-4 sm:px-6 sm:pt-6">
            <div className="min-w-0">
              {title && (
                <h2 className="font-display text-xl leading-tight text-heading">{title}</h2>
              )}
              {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
            </div>
            {dismissible && (
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="-mr-1 -mt-1 flex touch-target items-center justify-center rounded-full text-2xl leading-none text-ink-muted active:bg-oat sm:hover:bg-oat"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-6">{children}</div>

        {footer && (
          <div className="border-t border-oat-dark bg-surface/60 px-5 py-4 sm:px-6">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
